import { Brain } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import type { QuoteBuyerAnalysis } from "@/types";
import { formatValidityDate } from "@/utils/date";

export default function QuoteBuyerAnalysis({
  data,
}: {
  data?: QuoteBuyerAnalysis;
}) {
  // Determine quote assessment based on AI risk level
  // Maps risk levels to user-friendly status messages and confidence scores
  const getAssessment = () => {
    if (!data?.ai_insights)
      return { status: "Información limitada", confidence: 60 };

    const riskLevel = data.ai_insights.risk_level ?? "medium";
    if (riskLevel === "low")
      return { status: "Condiciones favorables", confidence: 85 };
    if (riskLevel === "medium")
      return { status: "Requiere revisión", confidence: 70 };
    return { status: "Condiciones complejas", confidence: 55 };
  };

  const assessment = getAssessment();
  // Use AI-provided confidence if available, otherwise fall back to assessment confidence
  const confidence = data?.analysis_metadata?.confidence
    ? Math.round(data.analysis_metadata.confidence * 100)
    : assessment.confidence;

  // Pre-compute formatted date to avoid multiple function calls
  const formattedValidityDate = formatValidityDate(
    data?.quote_info?.quotation_validity,
  );

  const normalize = (value?: string | null) => {
    if (!value) return "No encontrado";
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : "No encontrado";
  };

  // Build list of available insights, filtering out empty values
  const availableInsights = [
    data?.ai_insights?.payment_assessment
      ? {
          label: "Condiciones de Pago",
          value: data.ai_insights.payment_assessment,
        }
      : null,
    data?.ai_insights?.delivery_assessment
      ? {
          label: "Plazos de Entrega",
          value: data.ai_insights.delivery_assessment,
        }
      : null,
    data?.ai_insights?.overall_recommendation
      ? {
          label: "Recomendación",
          value: data.ai_insights.overall_recommendation,
        }
      : null,
  ]
    .filter(
      (insight): insight is { label: string; value: string } =>
        insight !== null,
    )
    .slice(0, 3);

  return (
    <Card className="border-slate-200 bg-slate-50/50">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-900 rounded-lg">
              <Brain className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Análisis IA</h3>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-slate-600 rounded-full transition-all duration-300"
                    style={{ width: `${confidence}%` }}
                  />
                </div>
                <span className="text-xs text-slate-500">{confidence}%</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-slate-900">
              {assessment.status}
            </div>
            {/* Show validity date if available and properly formatted */}
            {formattedValidityDate && (
              <div className="text-xs text-slate-500 mt-1">
                Válida hasta {formattedValidityDate}
              </div>
            )}
          </div>
        </div>

        {data?.summary_text && (
          <div className="mb-6 p-4 bg-white/60 border border-slate-200/50 rounded-lg">
            <p className="text-sm text-slate-700 leading-relaxed">
              {data.summary_text}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-white border border-slate-200/60 rounded-lg">
            <p className="text-xs uppercase text-slate-500 tracking-wide mb-1">
              Forma de pago
            </p>
            <p className="text-sm text-slate-800">
              {normalize(data?.quote_info?.payment_method)}
            </p>
          </div>
          <div className="p-4 bg-white border border-slate-200/60 rounded-lg">
            <p className="text-xs uppercase text-slate-500 tracking-wide mb-1">
              Plazo de pago
            </p>
            <p className="text-sm text-slate-800">
              {data?.quote_info?.payment_term_days ? `${data.quote_info.payment_term_days} días` : "No especificado"}
            </p>
          </div>
          <div className="p-4 bg-white border border-slate-200/60 rounded-lg">
            <p className="text-xs uppercase text-slate-500 tracking-wide mb-1">
              Plazo de entrega
            </p>
            <p className="text-sm text-slate-800">
              {normalize(data?.quote_info?.delivery_time)}
            </p>
          </div>
          <div className="p-4 bg-white border border-slate-200/60 rounded-lg">
            <p className="text-xs uppercase text-slate-500 tracking-wide mb-1">
              Validez del presupuesto
            </p>
            <p className="text-sm text-slate-800">
              {normalize(data?.quote_info?.validity_text)}
            </p>
          </div>
        </div>

        {availableInsights.length > 0 && (
          <div className="space-y-3">
            {availableInsights.map((insight, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0"></div>
                <div className="flex-1">
                  <span className="text-sm font-medium text-slate-700">
                    {insight.label}:
                  </span>
                  <span className="text-sm text-slate-600 ml-2">
                    {insight.value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
